import React, { type InputHTMLAttributes, forwardRef } from 'react';
import { Typewriter } from '../../contexts/TypewriterContext';

type RadiosProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'type'
> & {
  wrapperClassName?: string;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  labelPosition?: 'left' | 'right';
  variant?: 'default';
  effect?: 'typewriter' | 'none';
};

const Radios = forwardRef<HTMLInputElement, RadiosProps>((props, ref) => {
  const {
    size = 'md',
    label,
    labelPosition = 'right',
    className = '',
    wrapperClassName = '',
    disabled = false,
    checked: controlledChecked = false,
    effect = 'none',
    onChange,
    ...rest
  } = props;

  const checked = controlledChecked;

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const iconClasses = {
    sm: 'w-[60%] h-[60%] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary-400 rounded-full',
    md: 'min-w-[0.65rem] min-h-[0.65rem] max-w-[0.65rem] max-h-[0.65rem] w-[0.65rem] h-[0.65rem] absolute sm:top-[0.2rem] sm:left-[0.2rem] top-[0.16rem] left-[0.15rem] bg-primary-400 rounded-full',
    lg: 'w-[70%] h-[70%] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary-400 rounded-full',
  };

  const baseClasses =
    'relative inline-flex items-center justify-center rounded-full border-2 transition-all duration-200 cursor-pointer focus:outline-none';

  const stateClass = `border-primary-400 bg-white hover:bg-primary-50 focus:bg-white focus:ring-2 focus:ring-primary-200`;

  const disabledClass = 'border-gray-200 !cursor-not-allowed';

  const labelClasses = {
    default: 'text-gray-900 cursor-pointer',
    disabled: 'text-gray-400 !cursor-not-allowed',
  };

  const computedInputClasses = `${baseClasses} ${sizeClasses[size]} ${className}`;
  const computedLabelClasses = `${labelClasses[disabled ? 'disabled' : 'default']} select-none`;

  const checkboxElement = (
    <div className='relative inline-flex items-center'>
      <input
        ref={ref}
        type='radio'
        className='sr-only'
        disabled={disabled}
        checked={checked}
        onChange={onChange}
        {...rest}
      />
      <div
        role='radio'
        tabIndex={0}
        aria-checked={checked}
        className={`
          ${computedInputClasses}
          ${disabled ? disabledClass : stateClass}
        `}
      >
        {checked && (
          <div
            className={`${iconClasses[size]} ${disabled ? 'cursor-not-allowed' : ''}`}
          />
        )}
      </div>
    </div>
  );

  if (!label) {
    return <div className='group'>{checkboxElement}</div>;
  }

  return (
    <label
      className={`group flex items-center gap-2 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${wrapperClassName}`}
    >
      {labelPosition === 'left' &&
        (effect === 'typewriter' ? (
          <Typewriter
            as={'span'}
            text={label}
            className={computedLabelClasses}
          />
        ) : (
          <span className={computedLabelClasses}>{label}</span>
        ))}
      {checkboxElement}
      {labelPosition === 'right' &&
        (effect === 'typewriter' ? (
          <Typewriter
            as={'span'}
            text={label}
            className={computedLabelClasses}
          />
        ) : (
          <span className={computedLabelClasses}>{label}</span>
        ))}
    </label>
  );
});

Radios.displayName = 'Radios';

export default Radios;
